import { FC, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BookingUnitType } from '@services';
import { T_KEYS } from '../../constants';

// Building icon for multi-unit property (matching Figma)
const MultiUnitIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="4" width="20" height="24" rx="2" stroke="#5B3FFF" strokeWidth="2"/>
    <rect x="10" y="8" width="5" height="4" rx="1" fill="#5B3FFF"/>
    <rect x="17" y="8" width="5" height="4" rx="1" fill="#5B3FFF"/>
    <rect x="10" y="14" width="5" height="4" rx="1" fill="#5B3FFF"/>
    <rect x="17" y="14" width="5" height="4" rx="1" fill="#5B3FFF"/>
    <rect x="10" y="20" width="5" height="4" rx="1" fill="#5B3FFF"/>
    <rect x="17" y="20" width="5" height="4" rx="1" fill="#5B3FFF"/>
  </svg>
);

// Separate units icon (matching Figma)
const SingleUnitIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="10" height="12" rx="2" stroke="#5B3FFF" strokeWidth="2"/>
    <rect x="18" y="6" width="10" height="12" rx="2" stroke="#5B3FFF" strokeWidth="2"/>
    <rect x="11" y="14" width="10" height="12" rx="2" stroke="#5B3FFF" strokeWidth="2"/>
    <rect x="6" y="9" width="6" height="3" rx="1" fill="#5B3FFF"/>
    <rect x="20" y="9" width="6" height="3" rx="1" fill="#5B3FFF"/>
    <rect x="13" y="17" width="6" height="3" rx="1" fill="#5B3FFF"/>
  </svg>
);

// Left arrow icon for card (pointing left in RTL)
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M15 18L9 12L15 6" 
      stroke="#666666" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Back arrow icon for header (right arrow in RTL, pointing right to go back)
const BackArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M9 6L15 12L9 18" 
      stroke="#262626" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

interface BookingUnitTypeStepProps {
  onSelect: (unitType: BookingUnitType) => void;
  onBack?: () => void;
}

const BookingUnitTypeStep: FC<BookingUnitTypeStepProps> = ({
  onSelect,
  onBack,
}) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<BookingUnitType | null>(null);

  const handleSelect = useCallback((type: BookingUnitType) => {
    setSelectedType(type);
    onSelect(type);
  }, [onSelect]);

  return (
    <Container>
      <Header>
        <HeaderContent>
          {onBack && (
            <BackButton onClick={onBack}>
              <BackArrowIcon />
            </BackButton>
          )}
          <HeaderText>
            <Title>{t(T_KEYS.BOOKING_UNIT_TYPE_TITLE)}</Title>
            <Description>{t(T_KEYS.BOOKING_UNIT_TYPE_DESC)}</Description>
          </HeaderText>
        </HeaderContent>
      </Header>

      <CardsContainer>
        <OptionCard 
          $isSelected={selectedType === 'single'}
          onClick={() => handleSelect('single')}
        >
          <CardContent>
            <IconWrapper>
              <SingleUnitIcon />
            </IconWrapper>
            <CardTextContent>
              <CardTitle>{t(T_KEYS.MULTIPLE_SEPARATE_UNITS)}</CardTitle>
              <CardDescription>
                {t(T_KEYS.MULTIPLE_SEPARATE_UNITS_DESC)}
              </CardDescription>
            </CardTextContent>
          </CardContent>

          <CardArrow>
            <ArrowLeftIcon />
          </CardArrow>
        </OptionCard>

        <OptionCard 
          $isSelected={selectedType === 'multi'}
          onClick={() => handleSelect('multi')}
        >
          <CardContent>
            <IconWrapper>
              <MultiUnitIcon />
            </IconWrapper>
            <CardTextContent>
              <CardTitle>{t(T_KEYS.SINGLE_MULTI_UNIT_PROPERTY)}</CardTitle>
              <CardDescription>
                {t(T_KEYS.SINGLE_MULTI_UNIT_PROPERTY_DESC)}
              </CardDescription>
            </CardTextContent>
          </CardContent>

          <CardArrow>
            <ArrowLeftIcon />
          </CardArrow>
        </OptionCard>
      </CardsContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 1116px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 60px;
`;

const HeaderContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: flex-end;
  width: 100%;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 36px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    opacity: 0.7;
  }
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #262626;
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div<{ $isSelected: boolean }>`
  background: white;
  border: 1px solid ${({ $isSelected }) => ($isSelected ? '#5B3FFF' : '#f3f4f6')};
  border-radius: 24px;
  padding: 32px;
  display: flex;
  gap: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: flex-start;

  &:hover {
    border-color: #5B3FFF;
    box-shadow: 0 4px 12px rgba(91, 63, 255, 0.1);
  }
`;

const CardArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(250, 245, 255, 1) 0%, rgba(243, 232, 255, 0.5) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 2.667px 10.667px 0px rgba(0, 0, 0, 0.1);
`;

const CardTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const CardTitle = styled.h3`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: #262626;
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const CardDescription = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

export default BookingUnitTypeStep;
